import type { RefObject } from 'react';
import { Component, createRef } from 'react';

import throttle from 'lodash/throttle';

import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import { lowRes } from '../img';

import STYLES from './ProgressivePictureItem.module.scss';

import logger from '@shared/utils-frontend/logger';

type Props = {
  src: string;
  alt: string;
  className?: string;
  containerButtonClassName?: string;
  lowQualityClassName?: string;
  showShimmerAnimation?: boolean;
  lazyLoad?: boolean;
  sources: Array<{
    media?: string;
    srcSet: string[];
  }>;
};

type State = {
  imageQuality: string;
  inViewport: boolean;
};

const defaultProps = {
  showShimmerAnimation: false,
  lazyLoad: false,
  containerButtonClassName: null,
};

const cls = cssModules(STYLES);
const THROTTLE_TIME = 200;

class ProgressivePictureItem extends Component<Props, State> {
  static defaultProps = defaultProps;

  imageRef: RefObject<HTMLImageElement>;

  imageContainerRef: RefObject<HTMLDivElement>;

  throttleLazyLoadImage: any;

  lazyLoadObserver: IntersectionObserver | null;

  constructor(props: Props) {
    super(props);

    this.imageRef = createRef();
    this.imageContainerRef = createRef();
    this.state = {
      imageQuality: 'low',
      inViewport: false,
    };
    this.lazyLoadObserver = null;
  }

  componentDidMount() {
    const { lazyLoad } = this.props;
    if (lazyLoad) {
      this.initializeLazyLoadObserver();
    } else {
      this.handleImageLoadedBeforeDidMount();
    }
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return (
      this.props.src !== nextProps.src ||
      this.state.inViewport !== nextState.inViewport ||
      this.state.imageQuality !== nextState.imageQuality
    );
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.throttleLazyLoadImage);
    window.removeEventListener('resize', this.throttleLazyLoadImage);
    if (this.lazyLoadObserver) {
      this.lazyLoadObserver.disconnect();
    }
  }

  handleImageLoadedBeforeDidMount = () => {
    if (this.imageRef.current && this.imageRef.current.complete) {
      this.setState({
        imageQuality: 'high',
      });
    }
  };

  initializeLazyLoadObserver = () => {
    if (!('IntersectionObserver' in window)) {
      this.lazyLoadImage();
      this.throttleLazyLoadImage = throttle(this.lazyLoadImage, THROTTLE_TIME);
      (window as Window).addEventListener(
        'scroll',
        this.throttleLazyLoadImage,
        true,
      );
      (window as Window).addEventListener('resize', this.throttleLazyLoadImage);
    } else {
      this.lazyLoadObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.setState({
                inViewport: true,
              });
              this.lazyLoadObserver?.unobserve(entry.target);
            }
          });
        },
        {
          root: null,
          rootMargin: '0px',
          threshold: 0.1,
        },
      );

      if (this.imageContainerRef.current) {
        this.lazyLoadObserver.observe(this.imageContainerRef.current);
      }
    }
  };

  lazyLoadImage = () => {
    if (
      (!this.imageRef.current ||
        (this.imageRef.current && !this.imageRef.current.complete)) &&
      this.imageContainerRef.current
    ) {
      // if image has loaded, not excuted this code again
      const clientHeight = window.innerHeight;
      const bound = this.imageContainerRef.current.getBoundingClientRect();

      if (bound.top <= clientHeight) {
        this.setState({
          inViewport: true,
        });
      }
    }
  };

  onImageLoad = () => {
    this.setState({
      imageQuality: 'high',
    });
  };

  onImageError = () => {
    logger.warn('Failed to retrieve image', {
      src: this.props.src,
    });
  };

  render() {
    const { imageQuality, inViewport } = this.state;
    const {
      alt,
      className,
      containerButtonClassName,
      lazyLoad,
      lowQualityClassName,
      showShimmerAnimation,
      sources,
      src,
    } = this.props;

    const supportImageResize = src?.includes('_WxH');

    const imageNode = (
      <picture
        className={cls('ProgressivePictureItem__img')}
        style={{
          opacity: imageQuality === 'high' ? 1 : 0,
        }}
      >
        {supportImageResize &&
          sources.map((source) => (
            <source
              key={source.srcSet.join(',')}
              media={source.media}
              srcSet={source.srcSet.join(', ')}
            />
          ))}
        <img
          className={className}
          src={src}
          alt={showShimmerAnimation ? alt : ''}
          ref={this.imageRef}
          onLoad={this.onImageLoad}
          onError={this.onImageError}
        />
      </picture>
    );

    return (
      <div
        ref={this.imageContainerRef}
        className={cls('ProgressivePictureItem', containerButtonClassName)}
      >
        {!showShimmerAnimation && (
          <img
            className={cls(
              className,
              lowQualityClassName,
              'ProgressivePictureItem__img',
              'ProgressivePictureItem__lowQuality',
            )}
            src={lowRes}
            alt={alt}
            style={{
              opacity: imageQuality === 'low' ? 1 : 0,
            }}
          />
        )}

        {showShimmerAnimation && (
          <section
            className={cls(
              className,
              'ProgressivePictureItem__img',
              'ProgressivePictureItem__shimmer',
            )}
            style={{
              opacity: imageQuality === 'low' ? 1 : 0,
            }}
          />
        )}
        {lazyLoad && inViewport && imageNode}
        {!lazyLoad && imageNode}
      </div>
    );
  }
}

export default ProgressivePictureItem;
